import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "./ui/dialog";
import { toast } from "sonner";
import {
  Scale, Save, FileText, FileDown, Search, Calendar, Loader2, ChevronDown, ChevronRight,
  CalendarDays, Briefcase, Plus, Trash2, Users, Building2,
} from "lucide-react";
import PretrialJudgesPanel from "./PretrialJudgesPanel";
import { SectorPinakiaPicker } from "./SectorPinakiaPicker";
import { ActIssueDialog } from "./ActIssueDialog";
import { downloadActFromUrl } from "./actDownloads";
import { DateInput } from "./ui/date-input";
import { JudicialYearBar } from "./JudicialYearBar";

// Default fallback — η πραγματική λίστα φορτώνεται από /api/pretrial-tools/judge-grades
const DEFAULT_GRADES = [
  "Πάρεδρος Πρωτοδικών",
  "Πρωτοδίκης",
  "Πρωτοδίκης Γενικής Επετηρίδας",
  "Πρωτοδίκης Ειδικής Επετηρίδας",
  "Πρόεδρος Πρωτοδικών",
  "Πρόεδρος Πρωτοδικών Γενικής Επετηρίδας",
  "Πρόεδρος Πρωτοδικών Ειδικής Επετηρίδας",
  "Εφέτης",
  "Εφέτης Γενικής Επετηρίδας",
  "Εφέτης Ειδικής Επετηρίδας",
  "Προεδρεύων Εφέτης",
  "Πρόεδρος Εφετών",
];

const fmt = (d) => {
  if (!d) return "—";
  const parts = String(d).split("-");
  return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : d;
};

const downloadBlob = async (url, fallbackName) => {
  const res = await axios.get(url, { responseType: "blob" });
  const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
  const cd = res.headers["content-disposition"] || "";
  const m = cd.match(/filename="?([^"]+)"?/);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = m ? m[1] : fallbackName;
  document.body.appendChild(a); a.click(); a.remove();
  window.URL.revokeObjectURL(blobUrl);
};

// Τυπική αρχική δομή Πολυμελούς: 3 τακτικά + 2 αναπληρωματικά
const buildInitialComposition = (label = "Σύνθεση") => ({
  id: `comp_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
  label,
  members: [
    { name: "", grade: "Πρόεδρος Πρωτοδικών", role: "regular" },
    { name: "", grade: "Πρωτοδίκης", role: "regular" },
    { name: "", grade: "Πρωτοδίκης", role: "regular" },
    { name: "", grade: "Πρωτοδίκης", role: "alternate" },
    { name: "", grade: "Πρωτοδίκης", role: "alternate" },
  ],
});

const SectorsAndActsTab = () => {
  const { user } = useAuth();
  const userCourtTypes = user?.court_types || ["Μονομελές", "Πολυμελές"];
  const isAdmin = user?.role === "admin";

  const [courtType, setCourtType] = useState(userCourtTypes[0] || "Μονομελές");
  const [sectors, setSectors] = useState([]);
  // user (Νοέ 2026): δικαστικό έτος 15/9 → 14/9 — ενεργό έτος = pretrial_sectors, ιστορικά έτη = pretrial_sectors_history
  const [jy, setJy] = useState(null);
  const [judicialYear, setJudicialYear] = useState("");
  const isActiveYear = !jy || !judicialYear || judicialYear === jy.active;
  const [loading, setLoading] = useState(false);
  const [edits, setEdits] = useState({}); // sector_id -> partial overrides
  const [grades, setGrades] = useState(DEFAULT_GRADES);

  // Search by judge_start date
  const [searchDate, setSearchDate] = useState(new Date().toISOString().slice(0, 10));
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [expanded, setExpanded] = useState({});

  // Designator Calendar Dialog (per-sector, date range)
  const [dcDialogOpen, setDcDialogOpen] = useState(false);
  const [dcSector, setDcSector] = useState(null); // null = όλα
  const [dcFrom, setDcFrom] = useState("");
  const [dcTo, setDcTo] = useState("");
  const [dcLoading, setDcLoading] = useState(false);

  const fetchJy = async () => {
    try {
      const r = await axios.get(`${API}/judicial-years`);
      setJy(r.data);
      setJudicialYear((cur) => cur || r.data.active);
      return r.data;
    } catch { return null; }
  };
  useEffect(() => { fetchJy(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { if (judicialYear) fetchSectors(); /* eslint-disable-next-line */ }, [courtType, judicialYear]);

  useEffect(() => {
    axios.get(`${API}/pretrial-tools/judge-grades`)
      .then(r => Array.isArray(r.data?.grades) && r.data.grades.length && setGrades(r.data.grades))
      .catch(() => {});
  }, []);

  const fetchSectors = async () => {
    setLoading(true);
    try {
      const r = isActiveYear
        ? await axios.get(`${API}/pretrial-sectors`, { params: { court_type: courtType } })
        : await axios.get(`${API}/judicial-years/${encodeURIComponent(judicialYear)}/sectors`, { params: { court_type: courtType } });
      const list = r.data?.sectors || [];
      setSectors(list);
      setEdits({});
      // iter93: αυτόματη συμπλήρωση email μελών Σύνθεσης από μητρώο Δικαστών/χρήστες (όπου είναι κενό)
      const missing = [...new Set(list.flatMap(sec => (sec.compositions || []).flatMap(c => (c.members || []).filter(m => m.name && !m.email).map(m => m.name))))];
      if (missing.length) {
        try {
          const er = await axios.get(`${API}/pretrial-sectors/email-lookup`, { params: { names: missing.join("|") } });
          const emails = er.data?.emails || {};
          if (Object.keys(emails).length) {
            setSectors(list.map(sec => ({
              ...sec,
              compositions: (sec.compositions || []).map(c => ({
                ...c, members: (c.members || []).map(m => (!m.email && emails[m.name]) ? { ...m, email: emails[m.name], email_auto: true } : m),
              })),
            })));
          }
        } catch (_) { /* noop */ }
      }
    } catch (e) {
      toast.error("Σφάλμα φόρτωσης τομέων");
    } finally {
      setLoading(false);
    }
  };

  // Helper: επιστρέφει το merged σύνολο τιμών για ένα sector (DB + local edits)
  const getMerged = (s) => ({ ...s, ...(edits[s.id] || {}) });

  // Designators helpers
  const ensureDesignatorsArray = (s) => {
    const m = getMerged(s);
    const arr = Array.isArray(m.designators) ? m.designators : [];
    return arr.length ? arr : (m.judge_name
      ? [{ name: m.judge_name, grade: m.judge_grade || "Πρόεδρος Πρωτοδικών" }]
      : []);
  };

  const addDesignator = (s) => {
    const arr = [...ensureDesignatorsArray(s), { name: "", grade: "Πρόεδρος Πρωτοδικών" }];
    setEdits(p => ({ ...p, [s.id]: { ...(p[s.id] || {}), designators: arr } }));
  };

  const removeDesignator = (s, idx) => {
    const arr = ensureDesignatorsArray(s).filter((_, i) => i !== idx);
    setEdits(p => ({ ...p, [s.id]: { ...(p[s.id] || {}), designators: arr } }));
  };

  const updateDesignator = (s, idx, field, value) => {
    const arr = ensureDesignatorsArray(s).map((d, i) => i === idx ? { ...d, [field]: value } : d);
    setEdits(p => ({ ...p, [s.id]: { ...(p[s.id] || {}), designators: arr } }));
  };

  // Compositions helpers (Πολυμελές only)
  const ensureCompositionsArray = (s) => {
    const m = getMerged(s);
    return Array.isArray(m.compositions) ? m.compositions : [];
  };

  const addComposition = (s) => {
    const arr = ensureCompositionsArray(s);
    const next = [...arr, buildInitialComposition(`Σύνθεση ${String.fromCharCode(65 + arr.length)}`)];
    setEdits(p => ({ ...p, [s.id]: { ...(p[s.id] || {}), compositions: next } }));
  };

  const removeComposition = (s, idx) => {
    const arr = ensureCompositionsArray(s).filter((_, i) => i !== idx);
    setEdits(p => ({ ...p, [s.id]: { ...(p[s.id] || {}), compositions: arr } }));
  };

  const updateCompositionLabel = (s, idx, value) => {
    const arr = ensureCompositionsArray(s).map((c, i) => i === idx ? { ...c, label: value } : c);
    setEdits(p => ({ ...p, [s.id]: { ...(p[s.id] || {}), compositions: arr } }));
  };

  const addMember = (s, ci, role) => {
    const arr = ensureCompositionsArray(s).map((c, i) => {
      if (i !== ci) return c;
      const members = [...(c.members || []), { name: "", grade: "Πρωτοδίκης", role, email: "" }];
      return { ...c, members };
    });
    setEdits(p => ({ ...p, [s.id]: { ...(p[s.id] || {}), compositions: arr } }));
  };

  const removeMember = (s, ci, mi) => {
    const arr = ensureCompositionsArray(s).map((c, i) => {
      if (i !== ci) return c;
      return { ...c, members: (c.members || []).filter((_, k) => k !== mi) };
    });
    setEdits(p => ({ ...p, [s.id]: { ...(p[s.id] || {}), compositions: arr } }));
  };

  const updateMember = (s, ci, mi, field, value) => {
    const arr = ensureCompositionsArray(s).map((c, i) => {
      if (i !== ci) return c;
      return {
        ...c,
        members: (c.members || []).map((m, k) => k === mi ? { ...m, [field]: value, ...(field === "email" ? { email_auto: false } : {}) } : m),
      };
    });
    setEdits(p => ({ ...p, [s.id]: { ...(p[s.id] || {}), compositions: arr } }));
  };

  const updateField = (sid, field, value) => {
    setEdits(prev => ({ ...prev, [sid]: { ...(prev[sid] || {}), [field]: value } }));
  };

  const saveSector = async (sector) => {
    const patch = edits[sector.id] || {};
    if (Object.keys(patch).length === 0) { toast.info("Δεν υπάρχουν αλλαγές"); return; }
    try {
      const merged = { ...sector, ...patch };
      const designators = Array.isArray(merged.designators) ? merged.designators : [];
      // Συγχρονισμός legacy judge_name/grade με τον πρώτο designator
      const primary = designators[0] || { name: merged.judge_name || "", grade: merged.judge_grade || "Πρόεδρος Πρωτοδικών" };
      const payload = {
        court_type: merged.court_type, sector_number: merged.sector_number,
        title: merged.title, short_title: merged.short_title,
        subjects: typeof merged.subjects === "string"
          ? merged.subjects.split(",").map(s => s.trim()).filter(Boolean)
          : (merged.subjects || []),
        pinakio_prefixes: typeof merged.pinakio_prefixes === "string"
          ? merged.pinakio_prefixes.split(",").map(s => s.trim()).filter(Boolean)
          : (merged.pinakio_prefixes || []),
        hide_designator_signature: !!merged.hide_designator_signature,
        judge_name: primary.name || "",
        judge_grade: primary.grade || "Πρόεδρος Πρωτοδικών",
        tsd_act_number: merged.tsd_act_number || "",
        designators,
        compositions: (Array.isArray(merged.compositions) ? merged.compositions : []).map(c => ({
          ...c, members: (c.members || []).map(({ email_auto, ...m }) => ({ ...m, email: (m.email || "").trim() })),
        })),
        pinakio_labels: Array.isArray(merged.pinakio_labels) ? merged.pinakio_labels : [],
      };
      if (isActiveYear) await axios.put(`${API}/pretrial-sectors/${sector.id}`, payload);
      else await axios.put(`${API}/judicial-years/${encodeURIComponent(judicialYear)}/sectors/${sector.id}`, payload);
      toast.success(`Τομέας ${sector.sector_number} ενημερώθηκε${isActiveYear ? "" : ` (δικαστικό έτος ${judicialYear})`}`);
      fetchSectors();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα αποθήκευσης");
    }
  };

  const runSearch = async () => {
    if (!searchDate) { toast.error("Δώστε ημερομηνία"); return; }
    setSearchLoading(true);
    try {
      const r = await axios.get(`${API}/pretrial-sectors/cases-by-date`, {
        params: { judge_start_date: searchDate, court_type: courtType },
      });
      setSearchResults(r.data);
      setExpanded({});
    } catch (e) {
      toast.error("Σφάλμα αναζήτησης");
    } finally {
      setSearchLoading(false);
    }
  };

  // Επιλογή designator/composition πριν την έκδοση
  const pickDesignatorAndComposition = (sector) => {
    const designators = (sector && Array.isArray(sector.designators)) ? sector.designators : [];
    const compositions = (sector && Array.isArray(sector.compositions)) ? sector.compositions : [];
    let designator_index = null;
    let composition_index = null;
    if (designators.length > 1) {
      const labels = designators.map((d, i) => `${i + 1}. ${d.name || "(κενό)"}${d.grade ? " — " + d.grade : ""}`).join("\n");
      const input = window.prompt(`Πολλαπλοί Πρόεδροι Προσδιορισμού. Επιλέξτε αριθμό (1-${designators.length}):\n${labels}`, "1");
      if (input === null) return null;
      const idx = parseInt(input, 10) - 1;
      if (!isNaN(idx) && idx >= 0 && idx < designators.length) designator_index = idx;
    }
    if (compositions.length > 1) {
      const labels = compositions.map((c, i) => `${i + 1}. ${c.label || `Σύνθεση ${i + 1}`} (${(c.members || []).length} μέλη)`).join("\n");
      const input = window.prompt(`Πολλαπλές Συνθέσεις. Επιλέξτε αριθμό (1-${compositions.length}):\n${labels}`, "1");
      if (input === null) return null;
      const idx = parseInt(input, 10) - 1;
      if (!isNaN(idx) && idx >= 0 && idx < compositions.length) composition_index = idx;
    }
    return { designator_index, composition_index };
  };

  // Έκδοση Πράξης ανά υπόθεση — Αρ. Πράξης + Ημερομηνία Έκδοσης στον διάλογο (Μονομελές & Πολυμελές)
  const [actDialog, setActDialog] = useState({ open: false, c: null, format: "docx", picks: null });
  const generateAct = (caseObj, format, sectorObj) => {
    const picks = pickDesignatorAndComposition(sectorObj);
    if (picks === null) return;
    setActDialog({ open: true, c: caseObj, format, picks });
  };
  const confirmGenerateAct = async ({ actNumber, issueDate, format, includeNote }) => {
    const caseObj = actDialog.c;
    const year = String(caseObj.gak_year || new Date().getFullYear());
    const params = { act_number: actNumber, issue_date: issueDate };
    if (includeNote !== undefined) params.pretrial_note = includeNote;
    if (actDialog.picks?.designator_index != null) params.designator_index = actDialog.picks.designator_index;
    if (actDialog.picks?.composition_index != null) params.composition_index = actDialog.picks.composition_index;
    const ok = await downloadActFromUrl(`${API}/pretrial-sectors/generate-act/${caseObj.id}`, params, format, `Πράξη ${actNumber}/${year}`);
    if (ok) {
      caseObj.assignment_act_number = actNumber;
      caseObj.assignment_act_year = year;
      setSearchResults(r => r ? { ...r } : r);
    }
    return ok;
  };

  const exportDailyCalendar = async () => {
    if (!searchDate) { toast.error("Δώστε ημερομηνία"); return; }
    try {
      await downloadBlob(
        `${API}/pretrial-sectors/daily-calendar?judge_start_date=${searchDate}&court_type=${encodeURIComponent(courtType)}`,
        `calendar_${searchDate}.pdf`
      );
      toast.success("Ημερολόγιο PDF έτοιμο");
    } catch (e) {
      toast.error("Σφάλμα εξαγωγής ημερολογίου");
    }
  };

  const openDesignatorCalendarDialog = (sectorNumber = null) => {
    setDcSector(sectorNumber);
    setDcFrom("");
    setDcTo("");
    setDcDialogOpen(true);
  };

  const exportDesignatorCalendar = async () => {
    setDcLoading(true);
    try {
      const params = new URLSearchParams({ court_type: courtType, format: "pdf" });
      if (dcSector) params.set("sector_number", String(dcSector));
      if (dcFrom) params.set("filing_from", dcFrom);
      if (dcTo) params.set("filing_to", dcTo);
      const suffix = dcSector ? `_sector${dcSector}` : "_all";
      const range = (dcFrom || dcTo) ? `_${dcFrom || "start"}_${dcTo || "end"}` : "";
      await downloadBlob(
        `${API}/pretrial-tools/designator-calendar-export?${params.toString()}`,
        `designator_calendar_${courtType}${suffix}${range}.pdf`
      );
      toast.success("Ημερολόγιο έτοιμο");
      setDcDialogOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα εξαγωγής");
    } finally {
      setDcLoading(false);
    }
  };

  const saveTsdKatartisi = async () => { /* removed — TSD is per-sector */ };
  // alias για να μην σπάσει τίποτα — δεν χρησιμοποιείται πλέον
  void saveTsdKatartisi;

  const isPoly = courtType === "Πολυμελές";

  return (
    <div className="space-y-6">
      <ActIssueDialog open={actDialog.open} onOpenChange={(o) => setActDialog(a => ({ ...a, open: o }))}
        title={`Πράξη Ορισμού ${courtType === "Πολυμελές" ? "Σύνθεσης" : "Δικαστή"} — ΓΑΚ ${actDialog.c?.gak_number || ""}/${actDialog.c?.gak_year || ""}`}
        year={actDialog.c?.gak_year} defaultActNumber={actDialog.c?.assignment_act_number || ""}
        initialFormat={actDialog.format} onConfirm={confirmGenerateAct} testPrefix="sa-act-issue" showNoteOption
        noteHint={courtType === "Πολυμελές" ? "ισχύει για Πράξεις Ορισμού Εισηγητή για προδικαστικό έλεγχο, όχι για Πράξη Σύνθεσης" : ""} />
      {/* Court type selector */}
      <Card>
        <CardContent className="pt-4 flex items-center gap-4 flex-wrap">
          <div className="flex-1 min-w-[220px]">
            <Label className="text-xs">Τύπος Δικαστηρίου</Label>
            <Select value={courtType} onValueChange={setCourtType} disabled={userCourtTypes.length === 1}>
              <SelectTrigger data-testid="sa-court-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {userCourtTypes.map(ct => <SelectItem key={ct} value={ct}>{ct}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Badge variant="outline" className="text-xs">
            {sectors.length} τομείς · {sectors.filter(s => (s.designators || []).length || s.judge_name).length} με Πρόεδρο
          </Badge>
        </CardContent>
        <CardContent className="pt-0">
          <JudicialYearBar jy={jy} judicialYear={judicialYear} onChange={setJudicialYear} courtType={courtType} isAdmin={isAdmin}
            onRefresh={async () => { await fetchJy(); await fetchSectors(); }} />
          <p className="text-[11px] text-slate-500 mt-2" data-testid="jy-cutoff-note">
            Οι υπάρχουσες συνθέσεις/Πρόεδροι αφορούν το δικαστικό έτος 2026-2027. Πράξεις ορισμού/ανάθεσης με λήξη 10ημέρου έως 14/09/2026 επανεκδίδονται χωρίς όνομα Δικαστή Προσδιορισμού (δεν υπάρχουν στοιχεία προηγούμενου έτους), εκτός αν καταχωρηθούν σε ιστορικό έτος.
          </p>
        </CardContent>
      </Card>

      {/* SECTORS LIST */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Briefcase className="w-5 h-5" />Τομείς Δικαίου & Πρόεδροι Προσδιορισμού</CardTitle>
          <CardDescription>
            {isAdmin
              ? "Διαχειριστείτε Προέδρους Προσδιορισμού (πολλαπλοί) και — για Πολυμελές — Συνθέσεις με τακτικά/αναπληρωματικά μέλη."
              : "Προβολή ορισμένων Προέδρων ανά τομέα (admin για επεξεργασία)."}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading ? (
            <div className="text-center py-8 text-slate-400"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div>
          ) : sectors.map(s => {
            const merged = getMerged(s);
            const designators = ensureDesignatorsArray(s);
            const compositions = ensureCompositionsArray(s);
            // Πολυμελές sector 4 = Ειδικό Εμπορικό → Πράξη Εισηγητή απευθείας, χωρίς Σύνθεση
            const skipComposition = isPoly && (merged.sector_number === 4 || merged.act_type === "rapporteur");
            return (
              <div key={s.id} className="border rounded-md p-3 space-y-3" data-testid={`sa-sector-${s.sector_number}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <Badge className="bg-blue-600 text-white">{s.sector_number}ο Τμήμα</Badge>
                    <h3 className="font-semibold text-sm">{s.short_title || s.title}</h3>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => openDesignatorCalendarDialog(s.sector_number)} data-testid={`sa-calendar-${s.sector_number}`} title="Ημερολόγιο Δικαστή Προσδιορισμού (αυτός ο τομέας) με date range">
                      <CalendarDays className="w-3.5 h-3.5" />
                    </Button>
                    {isAdmin && (
                      <>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 px-2 text-indigo-700 hover:bg-indigo-50"
                          onClick={async () => {
                            const newNum = window.prompt(`Αλλαγή αριθμού Τμήματος (τρέχων: ${s.sector_number})\nΕισάγετε νέο αριθμό (1-99):`, String(s.sector_number));
                            if (!newNum || isNaN(parseInt(newNum))) return;
                            const num = parseInt(newNum);
                            if (num === s.sector_number) return;
                            try {
                              await axios.patch(`${API}/pretrial-sectors/${s.id}/renumber`, { new_sector_number: num });
                              toast.success(`Άλλαξε σε ${num}ο Τμήμα`);
                              fetchSectors();
                            } catch (err) {
                              toast.error(err.response?.data?.detail || "Σφάλμα");
                            }
                          }}
                          data-testid={`sa-renumber-${s.sector_number}`}
                          title="Αλλαγή αριθμού Τμήματος"
                        >
                          #{s.sector_number}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => saveSector(s)} data-testid={`sa-save-${s.sector_number}`}>
                          <Save className="w-3.5 h-3.5 mr-1" /> Αποθήκευση
                        </Button>
                      </>
                    )}
                  </div>
                </div>
                <p className="text-xs text-slate-500">{s.title}</p>

                {/* TSD + subjects */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Αρ. Πράξης ΤΣΔ (παράγρ. Γ)</Label>
                    <Input
                      value={merged.tsd_act_number || ""}
                      onChange={ev => updateField(s.id, "tsd_act_number", ev.target.value)}
                      placeholder="π.χ. 12/2026"
                      disabled={!isAdmin}
                      data-testid={`sa-tsd-act-${s.sector_number}`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Λέξεις-κλειδιά αντικειμένου (comma-separated)</Label>
                    <Textarea
                      rows={1}
                      value={typeof merged.subjects === "string" ? merged.subjects : (merged.subjects || []).join(", ")}
                      onChange={ev => updateField(s.id, "subjects", ev.target.value)}
                      placeholder="π.χ. ΕΝΟΧΙΚΟ, ΕΜΠΟΡΙΚΟ"
                      disabled={!isAdmin}
                      className="text-xs"
                      data-testid={`sa-subjects-${s.sector_number}`}
                    />
                  </div>
                </div>

                {/* Πινάκια & Ειδικές Συμπεριφορές */}
                <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 mb-3 items-start">
                  <div>
                    <Label className="text-xs" title="Προθέματα πινακίων που αντιστοιχούν στον τομέα (πχ ΗΑΣ → 3ο τμήμα)">
                      Προθέματα Πινακίων (comma-separated)
                    </Label>
                    <Input
                      value={typeof merged.pinakio_prefixes === "string"
                        ? merged.pinakio_prefixes
                        : (merged.pinakio_prefixes || []).join(", ")}
                      onChange={ev => updateField(s.id, "pinakio_prefixes", ev.target.value)}
                      placeholder="π.χ. ΗΑΣ, ΕΙΔΕΜ"
                      disabled={!isAdmin}
                      className="text-xs"
                      data-testid={`sa-pinakio-prefixes-${s.sector_number}`}
                    />
                  </div>
                  <div className="flex items-end h-full pb-1">
                    <label className="flex items-center gap-2 text-xs cursor-pointer select-none" title="Αν είναι ενεργό, η Πράξη δεν εμφανίζει όνομα Δικαστή στην υπογραφή (πχ 3ο Μονομελούς)">
                      <input
                        type="checkbox"
                        checked={!!merged.hide_designator_signature}
                        onChange={ev => updateField(s.id, "hide_designator_signature", ev.target.checked)}
                        disabled={!isAdmin}
                        className="w-4 h-4 accent-amber-600"
                        data-testid={`sa-hide-signature-${s.sector_number}`}
                      />
                      <span>Απόκρυψη ονόματος υπογραφής</span>
                    </label>
                  </div>
                </div>

                {/* iter93: Πινάκια Τομέα (πλήρη labels) — «Τα πινάκιά μου» Υπευθύνων Τομέα */}
                <SectorPinakiaPicker
                  sector={merged}
                  courtType={courtType}
                  isAdmin={isAdmin}
                  onChange={(labels) => updateField(s.id, "pinakio_labels", labels)}
                />

                {/* Designators (πολλαπλοί) */}
                <div className="border-l-4 border-blue-300 bg-blue-50/30 pl-3 py-2 rounded-r">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-xs font-semibold text-blue-900">
                      Πρόεδροι Προσδιορισμού ({designators.length})
                    </Label>
                    {isAdmin && (
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-blue-700" onClick={() => addDesignator(s)} data-testid={`sa-add-designator-${s.sector_number}`}>
                        <Plus className="w-3.5 h-3.5 mr-1" /> Προσθήκη Προέδρου
                      </Button>
                    )}
                  </div>
                  {designators.length === 0 && (
                    <p className="text-xs text-slate-400 italic">Δεν έχει οριστεί Πρόεδρος. Πατήστε «Προσθήκη Προέδρου».</p>
                  )}
                  <div className="space-y-2">
                    {designators.map((d, di) => (
                      <div key={di} className="grid grid-cols-1 md:grid-cols-[1.2fr_1fr_0.55fr_0.6fr_auto] gap-2 items-end">
                        <div>
                          <Label className="text-[10px] text-slate-500">Ονοματεπώνυμο</Label>
                          <Input
                            value={d.name || ""}
                            onChange={ev => updateDesignator(s, di, "name", ev.target.value)}
                            placeholder="Δημήτριος Παπαδόπουλος"
                            disabled={!isAdmin}
                            data-testid={`sa-designator-name-${s.sector_number}-${di}`}
                          />
                        </div>
                        <div>
                          <Label className="text-[10px] text-slate-500">Βαθμός</Label>
                          <Select value={d.grade || ""} onValueChange={v => updateDesignator(s, di, "grade", v)} disabled={!isAdmin}>
                            <SelectTrigger data-testid={`sa-designator-grade-${s.sector_number}-${di}`}><SelectValue placeholder="Επιλογή..." /></SelectTrigger>
                            <SelectContent>
                              {grades.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-[10px] text-slate-500" title="Άρθρο υπογραφής στις Πράξεις: «Ο ΔΙΚΑΣΤΗΣ» / «Η ΔΙΚΑΣΤΗΣ» (κενό → «Ο/Η»)">
                            Φύλο (Ο/Η)
                          </Label>
                          <Select value={d.gender || "none"} onValueChange={v => updateDesignator(s, di, "gender", v === "none" ? "" : v)} disabled={!isAdmin}>
                            <SelectTrigger data-testid={`sa-designator-gender-${s.sector_number}-${di}`}><SelectValue placeholder="Ο/Η" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Ο/Η (χωρίς)</SelectItem>
                              <SelectItem value="m">Ο — Άνδρας</SelectItem>
                              <SelectItem value="f">Η — Γυναίκα</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-[10px] text-slate-500" title="Αρ. Πράξης Τριμελούς Συμβουλίου Διεύθυνσης που εξουσιοδοτεί τον Πρόεδρο">
                            ΠΤΣ (παράγρ. Γ)
                          </Label>
                          <Input
                            value={d.tsd_act_number || ""}
                            onChange={ev => updateDesignator(s, di, "tsd_act_number", ev.target.value)}
                            placeholder="π.χ. 12 ή 12/2026"
                            disabled={!isAdmin}
                            data-testid={`sa-designator-tsd-${s.sector_number}-${di}`}
                          />
                        </div>
                        {isAdmin && (
                          <Button size="sm" variant="ghost" className="h-9 px-2 text-red-600" onClick={() => removeDesignator(s, di)} data-testid={`sa-remove-designator-${s.sector_number}-${di}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Compositions (μόνο Πολυμελές, εκτός sector 4) */}
                {isPoly && !skipComposition && (
                  <div className="border-l-4 border-purple-300 bg-purple-50/30 pl-3 py-2 rounded-r">
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-xs font-semibold text-purple-900 flex items-center gap-1">
                        <Users className="w-3.5 h-3.5" /> Συνθέσεις Πολυμελούς ({compositions.length})
                      </Label>
                      {isAdmin && (
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-purple-700" onClick={() => addComposition(s)} data-testid={`sa-add-composition-${s.sector_number}`}>
                          <Plus className="w-3.5 h-3.5 mr-1" /> Προσθήκη Σύνθεσης (3 τακτικά + 2 αναπληρωματικά)
                        </Button>
                      )}
                    </div>
                    {compositions.length === 0 && (
                      <p className="text-xs text-slate-400 italic">Δεν έχει οριστεί Σύνθεση. Πατήστε «Προσθήκη Σύνθεσης» — θα δημιουργηθεί αρχική τυπική δομή που μπορείτε να επεξεργαστείτε.</p>
                    )}
                    <div className="space-y-3">
                      {compositions.map((comp, ci) => {
                        const regulars = (comp.members || []).map((m, idx) => ({ m, idx })).filter(({ m }) => (m.role || "regular") === "regular");
                        const alternates = (comp.members || []).map((m, idx) => ({ m, idx })).filter(({ m }) => m.role === "alternate");
                        return (
                          <div key={comp.id || ci} className="border border-purple-200 rounded p-2 bg-white" data-testid={`sa-composition-${s.sector_number}-${ci}`}>
                            <div className="flex items-center justify-between gap-2 mb-2">
                              <Input
                                value={comp.label || ""}
                                onChange={ev => updateCompositionLabel(s, ci, ev.target.value)}
                                placeholder="Περιγραφή Σύνθεσης (π.χ. Σύνθεση Α)"
                                disabled={!isAdmin}
                                className="h-8 text-sm font-semibold"
                              />
                              {isAdmin && (
                                <Button size="sm" variant="ghost" className="h-8 px-2 text-red-600" onClick={() => removeComposition(s, ci)} data-testid={`sa-remove-composition-${s.sector_number}-${ci}`}>
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              )}
                            </div>
                            {[
                              { title: "Τακτικά Μέλη", role: "regular", list: regulars, color: "emerald" },
                              { title: "Αναπληρωματικά Μέλη", role: "alternate", list: alternates, color: "amber" },
                            ].map(group => (
                              <div key={group.role} className="mb-2">
                                <div className="flex items-center justify-between">
                                  <Label className={`text-[10px] uppercase tracking-wider text-${group.color}-700 font-semibold`}>{group.title} ({group.list.length})</Label>
                                  {isAdmin && (
                                    <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => addMember(s, ci, group.role)} data-testid={`sa-add-member-${s.sector_number}-${ci}-${group.role}`}>
                                      <Plus className="w-3 h-3 mr-1" /> Προσθήκη
                                    </Button>
                                  )}
                                </div>
                                {group.list.length === 0 && (
                                  <p className="text-[11px] text-slate-400 italic pl-2">— κανένα μέλος —</p>
                                )}
                                <div className="space-y-1">
                                  {group.list.map(({ m, idx }) => (
                                    <div key={idx} className="grid grid-cols-1 md:grid-cols-[1.2fr_1fr_1.2fr_auto] gap-2 items-center">
                                      <Input
                                        value={m.name || ""}
                                        onChange={ev => updateMember(s, ci, idx, "name", ev.target.value)}
                                        placeholder="Ονοματεπώνυμο"
                                        disabled={!isAdmin}
                                        className="h-8 text-xs"
                                        data-testid={`sa-member-name-${s.sector_number}-${ci}-${idx}`}
                                      />
                                      <div className="relative order-3 md:order-none">
                                        <Input
                                          type="email"
                                          value={m.email || ""}
                                          onChange={ev => updateMember(s, ci, idx, "email", ev.target.value)}
                                          placeholder="email για ειδοποιήσεις"
                                          disabled={!isAdmin}
                                          className={`h-8 text-xs ${m.email_auto ? "border-emerald-300 bg-emerald-50/40" : ""}`}
                                          title={m.email_auto ? "Συμπληρώθηκε αυτόματα από το μητρώο Δικαστών/χρήστες — αποθηκεύστε για να διατηρηθεί" : ""}
                                          data-testid={`sa-member-email-${s.sector_number}-${ci}-${idx}`}
                                        />
                                        {m.email_auto && <span className="absolute right-2 top-1.5 text-[9px] text-emerald-700 font-semibold" data-testid={`sa-member-email-auto-${s.sector_number}-${ci}-${idx}`}>αυτόματα</span>}
                                      </div>
                                      <Select value={m.grade || ""} onValueChange={v => updateMember(s, ci, idx, "grade", v)} disabled={!isAdmin}>
                                        <SelectTrigger className="h-8 text-xs" data-testid={`sa-member-grade-${s.sector_number}-${ci}-${idx}`}>
                                          <SelectValue placeholder="Βαθμός..." />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {grades.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                                        </SelectContent>
                                      </Select>
                                      {isAdmin && (
                                        <Button size="sm" variant="ghost" className="h-7 px-2 text-red-500" onClick={() => removeMember(s, ci, idx)} data-testid={`sa-remove-member-${s.sector_number}-${ci}-${idx}`}>
                                          <Trash2 className="w-3 h-3" />
                                        </Button>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <p className="text-[11px] text-slate-400 italic">
                  Σημείωση: Ο ΤΣΔ είναι ο αριθμός της Πράξης εξουσιοδότησης από το Τριμελές Συμβούλιο Διεύθυνσης (εμφανίζεται στην παράγραφο Γ).
                  {isPoly && !skipComposition && " Οι Συνθέσεις χρησιμοποιούνται στην Πράξη Ορισμού Σύνθεσης και Ανάθεσης Εισηγητή."}
                </p>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* SEARCH + CALENDAR */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><CalendarDays className="w-5 h-5" />Αναζήτηση & Ημερολόγιο Ορισμών</CardTitle>
          <CardDescription>
            Επιλέξτε ημερομηνία έναρξης 10ημέρου ορισμού δικαστή. Οι υποθέσεις εμφανίζονται ομαδοποιημένες ανά τομέα.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-end gap-3 flex-wrap">
            <div>
              <Label className="text-xs">Ημερομηνία έναρξης 10ημέρου</Label>
              <DateInput value={searchDate} onChange={e => setSearchDate(e.target.value)} data-testid="sa-search-date" />
            </div>
            <Button onClick={runSearch} disabled={searchLoading} data-testid="sa-search-btn">
              {searchLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Search className="w-4 h-4 mr-1" />}
              Αναζήτηση
            </Button>
            <Button variant="outline" onClick={exportDailyCalendar} data-testid="sa-export-calendar">
              <FileDown className="w-4 h-4 mr-1" /> Εξαγωγή Ημερολογίου PDF
            </Button>
          </div>

          {searchResults && (
            <div className="space-y-3">
              <div className="text-sm bg-slate-50 border rounded-md p-2 flex items-center justify-between flex-wrap">
                <span>
                  <Calendar className="w-4 h-4 inline mr-1" />
                  <strong>Ημερομηνία:</strong> {fmt(searchResults.judge_start_date)} ·
                  <span className="ml-2"><strong>Σύνολο:</strong> {searchResults.total_cases} υποθέσεις</span>
                </span>
              </div>
              {(searchResults.sectors || []).map(sect => (
                <div key={sect.sector_number} className="border rounded-md" data-testid={`sa-result-sector-${sect.sector_number}`}>
                  <div className="px-3 py-2 bg-slate-50 flex items-center justify-between cursor-pointer" onClick={() => setExpanded(p => ({ ...p, [sect.sector_number]: !p[sect.sector_number] }))}>
                    <div className="flex items-center gap-2 flex-wrap">
                      {expanded[sect.sector_number] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      <Badge className="bg-blue-600 text-white text-[10px]">{sect.sector_number}ο Τμ.</Badge>
                      <span className="font-semibold text-sm">{sect.short_title || sect.title}</span>
                      <Badge variant="outline" className="text-[10px]">{sect.cases?.length || 0} υποθέσεις</Badge>
                      {(sect.designators?.length > 0) && (
                        <span className="text-xs text-slate-500">· {sect.designators.length} Πρόεδρ.</span>
                      )}
                      {(sect.compositions?.length > 0) && (
                        <span className="text-xs text-purple-600">· {sect.compositions.length} Συνθέσ.</span>
                      )}
                    </div>
                  </div>
                  {expanded[sect.sector_number] && (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-slate-100 text-slate-700">
                            <th className="p-2 text-left">ΓΑΚ/Έτος</th>
                            <th className="p-2 text-left">ΕΑΚ/Έτος</th>
                            <th className="p-2 text-center">Κατάθεση</th>
                            <th className="p-2 text-left">Αντικείμενο</th>
                            <th className="p-2 text-center">Αρ. Πράξης</th>
                            <th className="p-2 text-center">Ενέργειες</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sect.cases.map(c => (
                            <tr key={c.id} className="border-b last:border-0">
                              <td className="p-2 font-mono">ΓΑΚ {c.gak_number}/{c.gak_year}</td>
                              <td className="p-2 font-mono text-blue-700">ΕΑΚ {c.eak_number || "—"}/{c.gak_year}</td>
                              <td className="p-2 text-center font-mono text-xs">{fmt(c.filing_date)}</td>
                              <td className="p-2 text-xs">{c.subject || "—"}</td>
                              <td className="p-2 text-center font-mono text-xs">
                                {c.assignment_act_number ? (
                                  <Badge variant="outline" className="text-[10px] border-emerald-300 text-emerald-700 bg-emerald-50">
                                    {c.assignment_act_number}/{c.assignment_act_year || c.gak_year}
                                  </Badge>
                                ) : <span className="text-slate-300">—</span>}
                              </td>
                              <td className="p-2 text-center whitespace-nowrap">
                                <Button size="sm" variant="outline" className="h-7 px-2 mr-1" onClick={() => generateAct(c, "docx", sect)} title="Πράξη DOCX" data-testid={`sa-act-docx-${c.id}`}>
                                  <FileText className="w-3.5 h-3.5" />
                                </Button>
                                <Button size="sm" variant="outline" className="h-7 px-2 mr-1" onClick={() => generateAct(c, "pdf", sect)} title="Πράξη PDF" data-testid={`sa-act-pdf-${c.id}`}>
                                  <FileDown className="w-3.5 h-3.5" />
                                </Button>
                                <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => generateAct(c, "odt", sect)} title="Πράξη ODT" data-testid={`sa-act-odt-${c.id}`}>
                                  <Scale className="w-3.5 h-3.5" />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
              {(searchResults.unassigned?.length > 0) && (
                <div className="border rounded-md border-amber-300">
                  <div className="px-3 py-2 bg-amber-50 text-amber-800 text-sm">
                    <strong>Αταξινόμητες υποθέσεις:</strong> {searchResults.unassigned.length}
                    <span className="text-xs ml-2">(το αντικείμενό τους δεν αντιστοιχίστηκε σε κανέναν τομέα — προσθέστε keywords)</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ΔΙΚΑΣΤΕΣ ΠΡΟΔΙΚΑΣΤΙΚΟΥ ΕΛΕΓΧΟΥ */}
      <PretrialJudgesPanel courtType={courtType} />

      {/* DESIGNATOR CALENDAR DIALOG (date range filter) */}
      <Dialog open={dcDialogOpen} onOpenChange={setDcDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarDays className="w-5 h-5" />
              Ημερολόγιο Προέδρου Προσδιορισμού
              {dcSector ? <Badge className="bg-blue-600 text-white ml-2">{dcSector}ος Τομέας</Badge> : <Badge variant="outline" className="ml-2">Όλοι οι τομείς</Badge>}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">
              Επιλέξτε εύρος ημερομηνιών κατάθεσης αγωγής για τμηματική εξαγωγή (π.χ. ανά εβδομάδα/μήνα). Αφήστε κενά για πλήρη εξαγωγή.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Κατάθεση από</Label>
                <DateInput value={dcFrom} onChange={e => setDcFrom(e.target.value)} data-testid="sa-dc-from" />
              </div>
              <div>
                <Label className="text-xs">Κατάθεση έως</Label>
                <DateInput value={dcTo} onChange={e => setDcTo(e.target.value)} data-testid="sa-dc-to" />
              </div>
            </div>
            <div className="flex gap-2 text-[11px] flex-wrap">
              <span className="text-slate-500">Γρήγορα φίλτρα:</span>
              {[
                { label: "Σήμερα", days: 0 },
                { label: "7 ημέρες", days: 7 },
                { label: "30 ημέρες", days: 30 },
                { label: "90 ημέρες", days: 90 },
              ].map(p => (
                <button
                  key={p.label}
                  type="button"
                  onClick={() => {
                    const to = new Date().toISOString().slice(0, 10);
                    const from = new Date(Date.now() - p.days * 86400000).toISOString().slice(0, 10);
                    setDcFrom(from); setDcTo(to);
                  }}
                  className="text-blue-600 hover:underline"
                >{p.label}</button>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDcDialogOpen(false)}>Άκυρο</Button>
            <Button onClick={exportDesignatorCalendar} disabled={dcLoading} data-testid="sa-dc-export">
              {dcLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileDown className="w-4 h-4 mr-1" />}
              Εξαγωγή PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SectorsAndActsTab;
